import React, { createContext, useContext } from 'react'

interface Addon {
  id: string
  name: string
  price: number
  quantity: number
}

interface BillingContextType {
  billingCycle: 'yearly' | 'monthly'
  setBillingCycle: (cycle: 'yearly' | 'monthly') => void
  selectedAddons: Addon[]
  quantities: { [key: string]: number }
  currentPlan: {
    name: string
    monthlyPrice: number
    yearlyPrice: number
  }
  handleQuantityChange: (id: string, value: string) => void
  increaseQuantity: (id: string) => void
  decreaseQuantity: (id: string) => void
  addAddon: (addon: Addon) => void
  updateAddonQuantity: (addonId: string, newQuantity: number) => void
  getPlanPrice: () => number
  getAddonTotal: () => number
  getTotal: () => number
}

const BillingContext = createContext<BillingContextType | undefined>(undefined)

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [billingCycle, setBillingCycle] = React.useState<'yearly' | 'monthly'>('yearly')
  const [selectedAddons, setSelectedAddons] = React.useState<Addon[]>([])
  const [quantities, setQuantities] = React.useState<{ [key: string]: number }>({})

  // Current plan info
  const currentPlan = {
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
  }

  // Handle quantity changes
  const handleQuantityChange = (id: string, value: string) => {
    const quantity = Math.max(0, Math.min(99, parseInt(value) || 0))
    setQuantities({ ...quantities, [id]: quantity })
  }

  // Increase/decrease quantity
  const increaseQuantity = (id: string) => {
    const currentQty = quantities[id] || 0
    setQuantities({ ...quantities, [id]: currentQty + 1 })
  }

  const decreaseQuantity = (id: string) => {
    const currentQty = quantities[id] || 0
    if (currentQty > 0) {
      setQuantities({ ...quantities, [id]: currentQty - 1 })
    }
  }

  // Add addon to selection
  const addAddon = (addon: Addon) => {
    const qty = quantities[addon.id] || 0
    if (qty <= 0) return

    // Check if addon already exists
    const existingIndex = selectedAddons.findIndex((item) => item.id === addon.id)

    if (existingIndex >= 0) {
      // Update existing addon
      const updatedAddons = [...selectedAddons]
      updatedAddons[existingIndex] = { ...addon, quantity: qty }
      setSelectedAddons(updatedAddons)
    } else {
      // Add new addon
      setSelectedAddons([...selectedAddons, { ...addon, quantity: qty }])
    }

    // Don't reset quantity after adding - keep it for editing
  }

  // Update quantity of an already added addon directly from the summary
  const updateAddonQuantity = (addonId: string, newQuantity: number) => {
    const quantity = Math.max(0, Math.min(99, newQuantity))

    if (quantity === 0) {
      // Remove addon if quantity is set to 0
      setSelectedAddons(selectedAddons.filter((addon) => addon.id !== addonId))
      return
    }

    // Update the quantity
    const updatedAddons = selectedAddons.map((addon) => (addon.id === addonId ? { ...addon, quantity } : addon))

    setSelectedAddons(updatedAddons)
  }

  // Calculate totals
  const getPlanPrice = () => {
    return billingCycle === 'yearly' ? currentPlan.yearlyPrice : currentPlan.monthlyPrice
  }

  const getAddonTotal = () => {
    return selectedAddons.reduce((total, addon) => {
      const monthly = addon.price * (addon.quantity || 0)
      return total + (billingCycle === 'yearly' ? monthly * 12 : monthly)
    }, 0)
  }

  const getTotal = () => {
    return getPlanPrice() + getAddonTotal()
  }

  return (
    <BillingContext.Provider
      value={{
        billingCycle,
        setBillingCycle,
        selectedAddons,
        quantities,
        currentPlan,
        handleQuantityChange,
        increaseQuantity,
        decreaseQuantity,
        addAddon,
        updateAddonQuantity,
        getPlanPrice,
        getAddonTotal,
        getTotal,
      }}
    >
      {children}
    </BillingContext.Provider>
  )
}

export const useBilling = () => {
  const context = useContext(BillingContext)
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider')
  }
  return context
}
